/**
 * Theme engine — resolves, merges, and provides theme values.
 * Pure functions with no framework dependency.
 *
 * Default and preset theme VALUES live in tokens/tokens.json (shared across
 * all implementations via codegen) — only merging/contrast LOGIC lives here.
 */
import { DEFAULT_WIDGET_THEME, WIDGET_PRESET_THEMES } from "../generated/tokens.js";
import { getDominantColor } from "./gradient-utils.js";
export { DEFAULT_WIDGET_THEME, WIDGET_PRESET_THEMES };
// ─── Theme merging ───────────────────────────────────────────────────────────
/** Merge a partial user theme with the defaults. Returns a fully resolved theme. */
export function mergeWidgetTheme(theme) {
    return { ...DEFAULT_WIDGET_THEME, ...theme };
}
/** Merge a component-level theme with the widget theme as fallback. */
export function mergeComponentTheme(componentTheme, widgetTheme) {
    const base = widgetTheme ?? DEFAULT_WIDGET_THEME;
    return {
        primaryColor: base.primaryColor,
        primaryTextColor: base.primaryTextColor,
        backgroundColor: base.backgroundColor,
        surfaceColor: base.surfaceColor,
        textColor: base.textColor,
        mutedTextColor: base.mutedTextColor,
        borderColor: base.borderColor,
        errorColor: base.errorColor,
        successColor: base.successColor,
        fontFamily: base.fontFamily,
        borderRadius: base.borderRadius,
        buttonStyle: base.buttonStyle,
        ...componentTheme,
    };
}
// ─── Button radius ───────────────────────────────────────────────────────────
/** Get button border radius based on style */
export function getButtonRadius(style) {
    switch (style) {
        case "pill":
            return "9999px";
        case "square":
            return "6px";
        case "rounded":
        default:
            return "12px";
    }
}
// ─── Color contrast ──────────────────────────────────────────────────────────
/**
 * Calculate relative luminance of a color.
 * Handles both solid hex colors and gradient strings (uses dominant color).
 */
function getLuminance(hexColor) {
    const solid = getDominantColor(hexColor);
    const hex = solid.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}
/** Check if a color is considered "light" (for auto-contrast) */
export function isLightColor(hexColor) {
    try {
        return getLuminance(hexColor) > 0.5;
    }
    catch {
        return true;
    }
}
/** Get a contrasting text color (black or white) for a given background */
export function getContrastingTextColor(bgColor) {
    return isLightColor(bgColor) ? "#212121" : "#ffffff";
}
